import React, { useEffect, useRef, useState } from 'react';
import { Table, Tag, Button, Card, Statistic, Row, Col, Modal, Descriptions, message, Popconfirm, Empty, InputNumber, Switch, Space } from 'antd';
import { SoundOutlined, ReloadOutlined, ClockCircleOutlined, FieldTimeOutlined, PauseCircleOutlined } from '@ant-design/icons';
import request from '../utils/request';
import { fmtTime, fmtHM } from '../utils/format';

const statusMap: Record<string, { color: string; text: string }> = {
  PENDING: { color: 'blue', text: '排队中' },
  CALLING: { color: 'gold', text: '就诊中' },
  COMPLETED: { color: 'green', text: '已完成' },
  CANCELLED: { color: 'red', text: '已取消' },
  ABSENT: { color: 'orange', text: '已过号' },
  EXPIRED: { color: 'default', text: '已过期' },
};

// 今日挂号：实时队列 + 叫号操作 + 今日名单
const TodayRegs: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [queue, setQueue] = useState<any>(null);
  const [statusCount, setStatusCount] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  // 每日放号上限输入框：首次拿到队列数据时用已保存值初始化，之后不被轮询覆盖
  const [limitInput, setLimitInput] = useState<number | null>(null);
  // 平均每人就诊分钟数（等待时长估算基线）
  const [avgInput, setAvgInput] = useState<number | null>(null);

  useEffect(() => {
    if (queue) {
      setLimitInput((v) => v ?? (queue.dailyLimit ?? 0));
      setAvgInput((v) => v ?? (queue.avgConsultMinutes ?? 10));
    }
  }, [queue]);

  // 竞态防护：请求序号让过期响应直接丢弃；busy 标记使叫号/过号操作在途期间暂停应用轮询结果，
  // 避免旧数据把"当前叫号"覆盖回退、操作员再点导致跳号
  const reqSeq = useRef(0);
  const busy = useRef(false);
  const inFlight = useRef(false);

  const fetchAll = async (silent = false) => {
    if (inFlight.current) return; // 上一请求还在途，不叠加
    inFlight.current = true;
    const seq = ++reqSeq.current;
    if (!silent) setLoading(true);
    try {
      const res: any = await request.get('/registration/today');
      if (seq !== reqSeq.current || busy.current) return;
      setList(res.list || []);
      setQueue(res.queue);
      setStatusCount(res.statusCount || {});
    } finally {
      inFlight.current = false;
      if (!silent) setLoading(false);
    }
  };

  // 首次加载 + 每 15 秒静默自动刷新，名单变动无需手动点刷新
  useEffect(() => {
    fetchAll();
    const timer = setInterval(() => fetchAll(true), 15000);
    return () => clearInterval(timer);
  }, []);

  // 操作统一走 runOp：在途期间屏蔽轮询写状态，结束后强制静默刷新拿最新队列
  const runOp = async (fn: () => Promise<void>) => {
    busy.current = true;
    try {
      await fn();
    } catch {
      // request 内部已提示
    } finally {
      busy.current = false;
      fetchAll(true);
    }
  };

  const callNext = () =>
    runOp(async () => {
      setCalling(true);
      try {
        const res: any = await request.put('/registration/call-next');
        message.success(`已叫 ${res.currentNo} 号（${res.patient?.patientName || ''}）`);
      } finally {
        setCalling(false);
      }
    });

  const updateStatus = (id: number, next: 'COMPLETED' | 'CANCELLED') =>
    runOp(async () => {
      await request.put(`/registration/${id}/status`, { status: next });
      message.success('操作成功');
    });

  // 放号设置：每日上限 + 人均就诊时长合并一次保存；暂停发号开关仍即时生效
  const [savingSettings, setSavingSettings] = useState(false);
  const saveSettings = async () => {
    if (limitInput === null || avgInput === null || savingSettings) return;
    setSavingSettings(true);
    try {
      await request.put('/registration/settings', { dailyLimit: limitInput, avgConsultMinutes: avgInput });
      message.success('放号设置已保存');
      fetchAll();
    } finally {
      setSavingSettings(false);
    }
  };

  const togglePause = async (next: boolean) => {
    await request.put('/registration/settings', { pauseIssuing: next });
    message.success(next ? '已暂停发号，小程序端无法取号' : '已恢复发号');
    fetchAll();
  };

  // 过号：叫到未到，置为过号并自动叫下一位
  const markAbsent = (id: number) =>
    runOp(async () => {
      const res: any = await request.put(`/registration/${id}/absent`);
      message.success(res.nextQueueNo ? `已过号，自动叫到 ${res.nextQueueNo} 号` : '已过号，当前无排队中的号码');
    });

  // 重新呼叫：过号患者到院
  const recall = (id: number) =>
    runOp(async () => {
      await request.put(`/registration/${id}/recall`);
      message.success('已重新呼叫');
    });

  const showDetail = async (id: number) => {
    const res: any = await request.get(`/registration/${id}/detail`);
    setDetail(res);
  };

  const columns = [
    {
      title: '排队号', dataIndex: 'queueNo', width: 100,
      render: (v: number, record: any) => (
        <span style={record.status === 'CALLING' ? { color: '#d48806', fontWeight: 700, fontSize: 16 } : undefined}>
          {v} 号
        </span>
      ),
    },
    { title: '患者', dataIndex: 'patientName' },
    { title: '电话', dataIndex: 'patientPhone', render: (v: string) => v || '-' },
    { title: '挂号时间', dataIndex: 'createdAt', render: (v: string) => fmtHM(v) },
    { title: '挂号诉求', dataIndex: 'demand', ellipsis: true, render: (v: string) => v || '-' },
    {
      title: '状态', dataIndex: 'status', width: 120,
      render: (v: string, record: any) => (
        <Tag color={statusMap[v]?.color}>
          {v === 'ABSENT' && record.absentCount > 1
            ? `已过号×${record.absentCount}`
            : statusMap[v]?.text}
        </Tag>
      ),
    },
    {
      title: '操作', width: 280, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <>
          <Button size="small" onClick={() => showDetail(record.id)}>详情</Button>{' '}
          {(record.status === 'PENDING' || record.status === 'CALLING') && (
            <Popconfirm title="确认标记为已完成？" onConfirm={() => updateStatus(record.id, 'COMPLETED')}>
              <Button size="small" type="primary">完成</Button>{' '}
            </Popconfirm>
          )}
          {record.status === 'CALLING' && (
            <Popconfirm title="患者未到？确认过号并叫下一位？" onConfirm={() => markAbsent(record.id)}>
              <Button size="small">过号</Button>{' '}
            </Popconfirm>
          )}
          {record.status === 'ABSENT' && (
            <>
              <Popconfirm title="确认重新呼叫该患者？" onConfirm={() => recall(record.id)}>
                <Button size="small" type="primary">重新呼叫</Button>{' '}
              </Popconfirm>
              <Popconfirm title="确认取消该挂号？" onConfirm={() => updateStatus(record.id, 'CANCELLED')}>
                <Button size="small" danger>取消</Button>
              </Popconfirm>
            </>
          )}
          {record.status === 'PENDING' && (
            <Popconfirm title="确认取消该挂号？" onConfirm={() => updateStatus(record.id, 'CANCELLED')}>
              <Button size="small" danger>取消</Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>今日挂号</h2>
        <Button icon={<ReloadOutlined />} onClick={() => fetchAll()}>刷新</Button>
      </div>

      {/* 叫号台：三个核心数字 + 唯一主动作 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={5}><Statistic title="当前叫号" value={queue?.currentNo ?? '-'} suffix="号" valueStyle={{ color: '#065F46' }} /></Col>
          <Col span={5}><Statistic title="今日已取号" value={queue?.issuedNo ?? '-'} suffix="号" /></Col>
          <Col span={5}><Statistic title="等待人数" value={queue?.waitingCount ?? '-'} suffix="人" valueStyle={{ color: (queue?.waitingCount ?? 0) > 0 ? '#d48806' : undefined }} /></Col>
          <Col span={9} style={{ textAlign: 'right' }}>
            {/* 唯一会连带完成"就诊中"患者的破坏性按钮，必须二次确认 */}
            <Popconfirm
              title="叫下一位"
              description="当前就诊中的患者将被自动标记为已完成，确认叫下一位？"
              onConfirm={callNext}
              okText="确认叫号"
              cancelText="取消"
            >
              <Button type="primary" size="large" icon={<SoundOutlined />} loading={calling}>
                叫下一位
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* 放号设置：数字项合并保存，发号开关即时生效，发号余量摘要放右上角 */}
      <Card
        size="small"
        title={<Space><ClockCircleOutlined style={{ color: '#065F46' }} />放号设置</Space>}
        style={{ marginBottom: 16 }}
        extra={
          <Space size={8}>
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              今日已发 <b style={{ color: '#111827' }}>{queue?.issuedNo ?? 0}</b>
              {queue?.dailyLimit > 0 ? ` / 上限 ${queue.dailyLimit}` : '（不限）'}
            </span>
            {queue?.paused
              ? <Tag color="red" style={{ marginInlineEnd: 0 }}>已停号</Tag>
              : queue?.remaining != null && <Tag color="green" style={{ marginInlineEnd: 0 }}>剩余 {queue.remaining} 号</Tag>}
          </Space>
        }
      >
        <Row gutter={24} align="middle">
          <Col flex="0 0 auto">
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>每日放号上限</div>
            <Space size={6}>
              <InputNumber
                min={0}
                max={10000}
                precision={0}
                value={limitInput}
                onChange={(v) => setLimitInput(v ?? 0)}
                style={{ width: 100 }}
              />
              <span style={{ color: '#9ca3af', fontSize: 12 }}>0 = 不限</span>
            </Space>
          </Col>
          <Col flex="0 0 auto">
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
              <FieldTimeOutlined style={{ marginRight: 4 }} />平均每人就诊（分钟）
            </div>
            <Space size={6}>
              <InputNumber
                min={1}
                max={120}
                precision={0}
                value={avgInput}
                onChange={(v) => setAvgInput(v ?? 10)}
                style={{ width: 100 }}
              />
              <span style={{ color: '#9ca3af', fontSize: 12 }}>用于小程序"约 M 分钟"估算</span>
            </Space>
          </Col>
          <Col flex="0 0 auto">
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
              <PauseCircleOutlined style={{ marginRight: 4 }} />线上发号
            </div>
            <Popconfirm
              title={queue?.paused ? '确认恢复线上发号？' : '确认暂停发号？小程序端将无法取号'}
              onConfirm={() => togglePause(!queue?.paused)}
              okText="确认"
              cancelText="取消"
            >
              <Switch
                checked={!queue?.paused}
                checkedChildren="发号中"
                unCheckedChildren="已暂停"
              />
            </Popconfirm>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button type="primary" loading={savingSettings} onClick={saveSettings}>保存设置</Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}><Card size="small"><Statistic title="今日挂号总数" value={list.length} /></Card></Col>
        <Col span={5}><Card size="small"><Statistic title="排队中" value={statusCount.PENDING || 0} valueStyle={{ color: '#1677ff' }} /></Card></Col>
        <Col span={5}><Card size="small"><Statistic title="就诊中" value={statusCount.CALLING || 0} valueStyle={{ color: '#d48806' }} /></Card></Col>
        <Col span={5}><Card size="small"><Statistic title="已完成" value={statusCount.COMPLETED || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="已过号" value={statusCount.ABSENT || 0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
      </Row>

      {list.length === 0 && !loading ? (
        <Empty description="今日暂无挂号" style={{ marginTop: 48 }} />
      ) : (
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={false}
        />
      )}

      <Modal title="挂号详情" open={!!detail} footer={null} onCancel={() => setDetail(null)} width={640}>
        {detail && (
          <Descriptions column={2} size="small" bordered title="挂号信息">
            <Descriptions.Item label="ID">{detail.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.text}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="排队号">{detail.queueNo} 号</Descriptions.Item>
            <Descriptions.Item label="挂号日期">{detail.regDate?.split('T')[0]}</Descriptions.Item>
            <Descriptions.Item label="患者">{detail.patientName}</Descriptions.Item>
            <Descriptions.Item label="电话">{detail.patientPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="身份证号" span={2}>{detail.idCard || '-'}</Descriptions.Item>
            <Descriptions.Item label="挂号时间">{fmtTime(detail.createdAt, true)}</Descriptions.Item>
            <Descriptions.Item label="挂号诉求">{detail.demand || '-'}</Descriptions.Item>
          </Descriptions>
        )}
        {/* 登录人信息：一个账号可为多人挂号，此处展示操作挂号的小程序账号 */}
        {detail?.user && (
          <Descriptions
            column={2}
            size="small"
            bordered
            title="登录人信息（挂号账号）"
            style={{ marginTop: 16 }}
          >
            <Descriptions.Item label="用户ID">{detail.user.id}</Descriptions.Item>
            <Descriptions.Item label="昵称">{detail.user.nickname || '-'}</Descriptions.Item>
            <Descriptions.Item label="姓名">{detail.user.realName || '-'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{detail.user.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="身份证号" span={2}>{detail.user.idCard || '-'}</Descriptions.Item>
            <Descriptions.Item label="住址" span={2}>{detail.user.address || '-'}</Descriptions.Item>
            <Descriptions.Item label="注册时间" span={2}>
              {fmtTime(detail.user.createdAt, true)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default TodayRegs;
